import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select, { GroupBase, OptionsOrGroups } from "react-select";

import EnrollmentCard from "src/components/enrollmentCard";
import HubInputField from "src/components/inputType";

import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";

import { ToastTypes } from "src/types";
import { NPILookupResponse } from "NPILookup";
import { APIEndpoints } from "src/types/apiTypes";
import { EnrollmentPageUserForm } from "./types";
import { LoaderContext } from "src/context/loaderContext";
import { populatePrescriberEnrollmentForm, resetPrescriberFormFields } from "src/helpers/enrollment";
import ReactInputMask from "react-input-mask";

const EnrollmentPracticeForm: FC<EnrollmentPageUserForm> =
  ({ parentNpi, control, errors, getValues, setValue, resetField, trigger, prescriberIndex, specialityData, removePrescriber })
    : JSX.Element => {
    const [allSpecialites, setSpecialites] = useState<OptionsOrGroups<string, GroupBase<string>>>();
    const [preservedNpi, updatePreservedNpi] = useState<string>(parentNpi);

    const { showToast } = useContext(ToastContext);
    const { setLoader, hideLoader } = useContext(LoaderContext);

    /**
     * @ConvertingSpecialities THis will support select dropdown with search functionaltiy
     */
    useEffect(() => {
      const specialityList: { value: string; label: string; }[] = [];
      specialityData?.forEach((speciality) => {
        specialityList.push({
          value: speciality.code,
          label: speciality.name,
        });
      });

      setSpecialites(specialityList as unknown as OptionsOrGroups<string, GroupBase<string>>);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialityData]);



    /**
     * @NPILookup calling npi lookup for details
     * @first useFetch hook for api call
     * @second useFetch error handler
     * @third useFetch success handler
     * @fourth npi lookup api trigger
     */
    const { data: npiLookupData, error: npiLookupError, mutate: lookupNpi, removeErrorAndData: clearlookupNpi } = useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

    /** @third */
    useEffect(() => {
      if (npiLookupData || npiLookupError) {
        hideLoader();

        if (npiLookupData) {
          if (npiLookupData.doctor_data !== null) {
            showToast([`Doctor Data Found`], ToastTypes.INFO);
            populatePrescriberEnrollmentForm(setValue, npiLookupData, preservedNpi, prescriberIndex);
            trigger([`enrollment_doctors_attributes.${prescriberIndex}`]);
          }

          if (npiLookupData.doctor_data === null) {
            showToast([`No Data Found`], ToastTypes.ERROR);
            resetPrescriberFormFields(resetField, prescriberIndex);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npiLookupData, npiLookupError]);

    /** @fourth */
    const callNPILookup = () => {
      const npi = getValues(`enrollment_doctors_attributes.${prescriberIndex}.npi`);

      if (npi !== preservedNpi) {
        const npiError = errors && errors.enrollment_doctors_attributes && errors.enrollment_doctors_attributes[prescriberIndex]?.npi;
        if (
          !npi ||
          (npi && npi.length === 0) ||
          npiError
        )
          showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
        else {
          setLoader();
          updatePreservedNpi(npi);
          clearlookupNpi();
          const data = {
            'global_doctor[npi]': npi,
          };
          lookupNpi(null, null, data);
        }
      }
    };


    return (
      <EnrollmentCard isTextCenter={false} isCrossBtnVisible={prescriberIndex !== 0} index={prescriberIndex} clickHandlerForCrossBtn={removePrescriber} cardHeader="Prescriber">
        <>
          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.npi`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.npi`}
                    inputPlaceholder="NPI*"
                    isRequired={true}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                    onBlur={callNPILookup}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        callNPILookup();
                      }
                    }}
                  />
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.tax_id`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.tax_id`}
                    inputPlaceholder="Tax ID"
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                  />
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.firstname`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.firstname`}
                    inputPlaceholder="First Name*"
                    isRequired={true}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.lastname`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.lastname`}
                    inputPlaceholder="Last Name*"
                    isRequired={true}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                  />
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.middlename`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.middlename`}
                    inputPlaceholder="Middle Name"
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    field={field}
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-6 mb-3">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.ref_taxonomy_id`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      options={allSpecialites || []}
                      id={`enrollment_doctors_attributes.${prescriberIndex}.ref_taxonomy_id`}
                      classNames={{
                        control(_props) {
                          return `input-group ${error && error.message ? "border-danger" : ""}`
                        },
                      }}
                      placeholder="Ref Taxonomy *"
                      isSearchable={true}
                      {...field}
                      noOptionsMessage={() => "Select Option..."}
                      value={
                        allSpecialites?.find((option) => {
                          const casted = option as unknown as { value: string; label: string };
                          return casted.value === field.value;
                        }) ?? null
                      }
                      onChange={(value) => {
                        const selectedValue = (value as unknown as { value: string; label: string })?.value;
                        field.onChange(selectedValue);
                      }}
                      menuPortalTarget={document.body}
                      maxMenuHeight={250}
                      menuPlacement="auto"
                    />

                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2 "></div>
                    ) : null}
                  </>
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.phone_number`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className="row mb-3">
                    <div className="col-12">
                      <ReactInputMask
                        {...field}
                        mask="(999) 999-9999"
                        className={`form-control ${error ? "is-invalid" : ""}`}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      >
                        {(inputProps: {}) => <input type="text" id="enrollment_practices_attributes.0.phone_number"
                          className="form-control"
                          placeholder="Phone Number* e.g. (555) 555-5555" {...inputProps} />}
                      </ReactInputMask>

                      {error && error.message ? (
                        <div dangerouslySetInnerHTML={{ __html: error.message }}
                          className="text-danger text-small mt-2"></div>
                      ) : null}
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.fax_number`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <div className="row mb-3">
                    <div className="col-12">
                      <ReactInputMask
                        {...field}
                        mask="(999) 999-9999"
                        className={`form-control ${error ? "is-invalid" : ""}`}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      >
                        {(inputProps: {}) => <input type="text"
                          id="enrollment_practices_attributes.0.fax_number"
                          className="form-control"
                          placeholder="Fax Number* e.g. (555) 555-5555" {...inputProps} />}
                      </ReactInputMask>

                      {error && error.message ? (
                        <div dangerouslySetInnerHTML={{ __html: error.message }}
                          className="text-danger text-small mt-2"></div>
                      ) : null}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.sln`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.sln`}
                    inputPlaceholder="SLN - State License Number"
                    field={field}
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.dea`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.dea`}
                    inputPlaceholder="DEA Number"
                    field={field}
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    error={error}
                  />
                )}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.ptan`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.ptan`}
                    inputPlaceholder="Provider Transaction Access Number"
                    field={field}
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-6">
              <Controller
                name={`enrollment_doctors_attributes.${prescriberIndex}.email`}
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId={`enrollment_doctors_attributes.${prescriberIndex}.email`}
                    inputPlaceholder="Email"
                    field={field}
                    isRequired={false}
                    isRowBased={true}
                    isRowInsideAnother={true}
                    error={error}
                  />
                )}
              />
            </div>
          </div>
        </>
      </EnrollmentCard>
    );
  };

export default EnrollmentPracticeForm;
